/**
 * Blog data layer — direct port of blog.js's fetching logic, unchanged
 * in behavior. Every post is one HTML file in a GitHub repo's /blog
 * folder; this calls GitHub's public API to list and parse them, with
 * a fallback demo post so the page never looks broken/empty before a
 * real repo is wired up. No build step, no CMS — publishing a post is
 * just adding a file to the repo.
 *
 * To go live: fill in BLOG_CONFIG below with a real public GitHub repo.
 * Until then, blogConfigured() is false and every page shows the
 * fallback post plus an honest "Preview mode" notice — never a fake
 * "connected" state.
 */

export const BLOG_CONFIG = {
  owner: "ani-modus",
  repo: "modus-blog",
  branch: "main",
  folder: "blog",
};

const BLOG_CACHE_KEY = "modus-blog-cache-v1";
const BLOG_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export function blogConfigured() {
  return BLOG_CONFIG.owner !== "YOUR-GITHUB-USERNAME" && BLOG_CONFIG.repo !== "YOUR-REPO-NAME";
}

// A single demo post so the page never looks empty before the repo is
// wired up, and as a live example of what a rendered post looks like.
const BLOG_FALLBACK_POSTS = [
  {
    slug: "welcome-to-thoughtful-care",
    title: "Why We Measure Doctors on Outcomes, Not Codes",
    date: "2026-09-18",
    excerpt:
      "Most quality scores are built from billing codes. Modus reads what actually happened to the patient instead, and pays physicians accordingly.",
    cover: "",
    author: "Modus Team",
    bodyHTML: `
      <p>Every reimbursement system starts with the same unit: a code. ICD for what was wrong, CPT for what was done. It is a record of activity, not of outcome, and that gap is where most of healthcare's wasted spend hides.</p>
      <h2>What we measure instead</h2>
      <p>Modus Clinical Intelligence reads historical claims and public Medicare records for seven signals: surgery rate, opioid prescribing, testing utilization, postoperative hospital days, revision rates, one-year mortality, and legal standing. Every signal is peer-matched, same specialty, same state, and read only from elective, expensive procedures, where the decision to operate is itself the judgment being measured.</p>
      <h2>Why it changes behavior</h2>
      <p>A physician who knows their rating is built from what happened to their patients, not from how the chart was coded, has a very different incentive than one being graded on documentation completeness. That is the entire premise of the Modus Direct Contracted Network: find the physicians whose judgment already works, pay them what they ask, and keep measuring whether patients got better.</p>
      <p>This is a demo post shipped with the blog engine. Replace it by publishing your own posts to the connected GitHub repo.</p>
    `,
  },
];

function readCache() {
  try {
    const raw = sessionStorage.getItem(BLOG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.t > BLOG_CACHE_TTL_MS) return null;
    return parsed.posts;
  } catch (e) {
    return null;
  }
}

function writeCache(posts) {
  try {
    sessionStorage.setItem(BLOG_CACHE_KEY, JSON.stringify({ t: Date.now(), posts }));
  } catch (e) {
    // sessionStorage can fail (private browsing, quota) — caching is an
    // optimization, not a requirement, so just skip it silently.
  }
}

// A post's cover can be a full URL (the old convention, pointing at
// wherever the image happens to be hosted) or a path relative to this
// same /blog folder in the content repo (the convention the template
// documents, e.g. "images/my-cover.jpg"). Relative paths need to
// resolve against the content repo's raw file host, not against
// modus.org itself — the browser has no other way to know where a
// bare "images/..." path is supposed to point.
function resolveCoverUrl(raw) {
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://raw.githubusercontent.com/${BLOG_CONFIG.owner}/${BLOG_CONFIG.repo}/${BLOG_CONFIG.branch}/${BLOG_CONFIG.folder}/${raw.replace(/^\.?\//, "")}`;
}

// Body content can also contain relative image paths (the same
// "images/..." convention as post-cover), e.g. <img src="images/chart.png">.
// The browser has no idea those are relative to the content repo - it
// resolves them against whatever page URL is showing the post - so any
// relative <img src> inside the rendered body must be rewritten the same
// way resolveCoverUrl already rewrites the cover field, or the image
// silently 404s on every page except a raw view of the file itself.
function resolveBodyImages(html) {
  return html.replace(/(<img\s+[^>]*?src=["'])([^"']+)(["'])/gi, (match, pre, src, post) => {
    if (/^(https?:)?\/\//i.test(src) || src.startsWith("data:")) return match;
    return `${pre}${resolveCoverUrl(src)}${post}`;
  });
}

function parsePostHTML(html, slug) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const meta = (name) => doc.querySelector(`meta[name="post-${name}"]`)?.getAttribute("content") || "";
  const content = doc.querySelector("#post-content");
  let excerpt = meta("excerpt");
  if (!excerpt && content) {
    const firstP = content.querySelector("p");
    excerpt = firstP ? firstP.textContent.trim().slice(0, 160) : "";
  }
  return {
    slug,
    title: meta("title") || doc.querySelector("title")?.textContent || slug,
    date: meta("date") || "",
    excerpt,
    cover: resolveCoverUrl(meta("cover")),
    author: meta("author") || "Modus Team",
    bodyHTML: resolveBodyImages(content ? content.innerHTML : doc.body ? doc.body.innerHTML : ""),
  };
}

export async function fetchPostList() {
  const cached = readCache();
  if (cached) return cached;

  if (!blogConfigured()) return BLOG_FALLBACK_POSTS;

  try {
    const listRes = await fetch(
      `https://api.github.com/repos/${BLOG_CONFIG.owner}/${BLOG_CONFIG.repo}/contents/${BLOG_CONFIG.folder}?ref=${BLOG_CONFIG.branch}`
    );
    if (!listRes.ok) throw new Error(`GitHub API ${listRes.status}`);
    const items = await listRes.json();
    const files = items.filter((it) => it.type === "file" && it.name.endsWith(".html") && !it.name.startsWith("_"));

    const posts = await Promise.all(
      files.map(async (file) => {
        const raw = await fetch(file.download_url).then((r) => r.text());
        const slug = file.name.replace(/\.html$/, "");
        return parsePostHTML(raw, slug);
      })
    );

    posts.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    writeCache(posts);
    return posts;
  } catch (err) {
    console.info(
      "[Modus blog] Could not reach GitHub repo, showing demo content. Set BLOG_CONFIG in blogData.js. Details:",
      err.message
    );
    return BLOG_FALLBACK_POSTS;
  }
}

export async function fetchSinglePost(slug) {
  if (!blogConfigured()) {
    return BLOG_FALLBACK_POSTS.find((p) => p.slug === slug) || null;
  }
  try {
    const url = `https://raw.githubusercontent.com/${BLOG_CONFIG.owner}/${BLOG_CONFIG.repo}/${BLOG_CONFIG.branch}/${BLOG_CONFIG.folder}/${slug}.html`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`raw fetch ${res.status}`);
    const html = await res.text();
    return parsePostHTML(html, slug);
  } catch (err) {
    console.info(
      "[Modus blog] Could not load post, falling back to demo content if slug matches. Details:",
      err.message
    );
    return BLOG_FALLBACK_POSTS.find((p) => p.slug === slug) || null;
  }
}

export function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}